import { useEffect } from "react";
import { useCompanyStore } from "@/store";
import { type ThemePreference, type ResolvedTheme } from "@njbsoft/store";

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === "light" || pref === "dark") return pref;
  return getSystemTheme();
}

function applyTheme(theme: ResolvedTheme) {
  if (theme === "dark") {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
}

export function useThemeSync() {
  const themePreference = useCompanyStore((s) => s.themePreference);
  const setResolvedTheme = useCompanyStore((s) => s.setResolvedTheme);

  // Apply theme whenever preference changes
  useEffect(() => {
    const resolved = resolveTheme(themePreference);
    applyTheme(resolved);
    setResolvedTheme(resolved);
  }, [themePreference, setResolvedTheme]);

  // Listen for system theme changes when preference is "system"
  useEffect(() => {
    if (themePreference !== "system") return;

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      const resolved: ResolvedTheme = e.matches ? "dark" : "light";
      applyTheme(resolved);
      setResolvedTheme(resolved);
    };

    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [themePreference, setResolvedTheme]);
}
